<template>
  <div
    v-if="activeDialog"
    class="overlay"
    :class="activeDialog ? 'active': ''"
  >
    <div
      class="common-dialog-wrapper image-popup"
      :style="{ maxWidth: '90vw' }"
    >
      <div class="close-dialog" @click="closePopups">
        <WhiteCloseIcon28 />
      </div>
      <div class="popup-content">
        <div class="popup-header">
          <div class="client-info">
            <div class="contacts-name">
              <div class="img-item">
                <img
                  v-if="clientInfo.avatar"
                  :src="clientInfo.avatar"
                  alt="avatar"
                >
                <BlueUserIcon v-else />
              </div>
              <span class="span-name">{{ clientInfo.name }}</span>
            </div>
            <span class="device-info">{{ getDeviceName(clientDevice) + ' - ' + attachment.name }}</span>
          </div>
          <div v-if="tagsList.length" class="tag-wrap">
            <span
              v-for="(tag, index) in tagsList"
              :key="index"
              class="tag"
              v-text="tag"
            />
          </div>
        </div>
        <div class="image-wrapper">
          <div
            class="back-button"
            :class="{ 'disabled': currentIndex <= 0 }"
            @click="prevPage"
          >
            <arrowDown class="arrow" />
          </div>
          <div
            v-if="attachment.isBlured && !unblurredLink"
            class="popup-blured-content"
          >
            <WhiteOpenEyeIcon48 v-if="hasPermission(['view_origin_content'])" @click="showOriginContent" />
            <WhiteEyeStrikethroughIcon48 v-else />
          </div>
          <div v-else-if="isImageType" class="image-content">
            <div
              v-if="isHeicFormat(attachment.name) && (isConverting(attachment.link) || (unblurredLink && isConverting(unblurredLink)))"
              class="popup-conversion-loader"
            >
              <CircularLoader />
            </div>
            <img
              v-else
              :key="attachment.id || 'image-viewer'"
              class="popup-image"
              :src="imageUrl"
              alt="attachment"
            >
          </div>
          <div
            v-else-if="shouldShowVideoPlayer"
            ref="playerWrap"
            class="player-wrap"
            style="width: 100%; height: 65vh; max-height: 65vh;"
          >
            <div v-if="playerError" class="unsupported">
              <span>Video player failed to load</span>
            </div>
            <div v-else-if="!videoReady" class="loading-video">
              <span>Loading video player...</span>
            </div>
            <div v-else ref="playerContainer" class="video-container">
              <video
                v-if="useNativePlayer"
                ref="videoElement"
                width="100%"
                height="100%"
                controls
                class="native-video-player"
                @error="handlePlayerError"
              >
                <source :src="videoSrc" type="video/mp4">
                Your browser does not support the video tag.
              </video>

              <vue-plyr
                v-else
                ref="plyrPlayer"
                :key="`player-${currentIndex}-${attachment.id}`"
                @error="handlePlayerError"
              >
                <video
                  ref="plyrVideoElement"
                  width="100%"
                  height="100%"
                  @pause="isPaused = true"
                  @play="isPaused = false"
                  @error="handlePlayerError"
                >
                  <source
                    :src="videoSrc"
                    type="video/mp4"
                    class="video"
                  >
                </video>
              </vue-plyr>
            </div>
          </div>
          <div v-else class="unsupported">
            <span>The format is not supported</span>
          </div>
          <div
            class="forward-button"
            :class="{ 'disabled': currentIndex >= galleryItems.length - 1 }"
            @click="nextPage"
          >
            <arrowDown class="arrow" />
          </div>
        </div>
        <div class="menu-items">
          <SquarePinIcon @click="handleMenuOptionSelect('mark priority')" />
          <SquareBlurIcon
            v-if="hasPermission(['add_comments_highlights'])"
            @click="handleMenuOptionSelect('redact media')"
          />
          <SquareImportIcon @click="handleMenuOptionSelect('export')" />
          <SquareInfoIcon @click="handleMenuOptionSelect('show meta')" />
          <SquareBurgerMenuIcon @click="handleMenuOptionSelect('view details')" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BlueUserIcon24 from '@/components/icons/BlueUserIcon24.vue';
import WhiteEyeStrikethroughIcon48 from '@/components/icons/WhiteEyeStrikethroughIcon48.vue';
import WhiteOpenEyeIcon48 from '@/components/icons/WhiteOpenEyeIcon48.vue';
import SquarePinIcon from '@/components/icons/SquarePinIcon.vue';
import SquareBlurIcon from '@/components/icons/SquareBlurIcon.vue';
import SquareImportIcon from '@/components/icons/SquareImportIcon.vue';
import SquareInfoIcon from '@/components/icons/SquareInfoIcon.vue';
import SquareBurgerMenuIcon from '@/components/icons/SquareBurgerMenuIcon.vue';
import hasPermission from '@/utils/permission';
import WhiteCloseIcon28 from '@/components/icons/WhiteCloseIcon28.vue';
import ArrowDown from '@/components/icons/ArrowDown.vue';
import {mapGetters} from 'vuex';
import VuePlyr from 'vue-plyr';
import heicConversion from '@/mixins/heicConversion';
import CircularLoader from '@/components/loader/CircularLoader.vue';

export default {
  name: 'MediaItemPopup',
  components: {
    ArrowDown,
    WhiteCloseIcon28,
    WhiteOpenEyeIcon48,
    WhiteEyeStrikethroughIcon48,
    BlueUserIcon: BlueUserIcon24,
    SquareBurgerMenuIcon,
    SquareInfoIcon,
    SquareImportIcon,
    SquareBlurIcon,
    SquarePinIcon,
    VuePlyr,
    CircularLoader,
  },
  mixins: [heicConversion],
  props: {
    attachment: {
      type: Object,
      default: () => ({}),
    },
    attachmentType: {
      type: String,
      default: '',
    },
    tagsList: {
      type: Array,
      default: () => [],
    },
    unblurredLink: {
      type: String,
      default: '',
    },
    galleryItems: {
      type: Array,
      default: () => [],
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
  },
  emits: ['closed', 'menuOptionSelect', 'showOriginContent', 'pageChanged', 'resetUnblurred'],
  data() {
    return {
      activeDialog: false,
      isPaused: true,
      internalAttachmentType: '',
      playerError: false,
      videoReady: false,
      useNativePlayer: true,
      caseId: this.$route && this.$route.params ? this.$route.params.slug : null,
    };
  },
  computed: {
    ...mapGetters('mediaTagging', {
      clientDevice: 'getDeviceInfo',
      clientInfo: 'getClientInfo',
    }),
    isImageType() {
      return this.internalAttachmentType === 'image'
        && this.canViewBlurred
        && (this.attachment.convertedJpegUrl || this.attachment.link || this.unblurredLink);
    },
    canViewBlurred() {
      if (!this.attachment.isBlured) {
        return true;
      }
      return !!(this.attachment.isBlured
        && hasPermission(['view_origin_content'])
        && (this.unblurredLink || this.attachment.convertedJpegUrl));
    },
    shouldShowVideoPlayer() {
      return this.activeDialog &&
        this.internalAttachmentType === 'video' &&
        this.canViewBlurred &&
        (this.attachment.link || this.unblurredLink || this.attachment.convertedJpegUrl);
    },
    videoSrc() {
      return this.attachment.link || this.unblurredLink || this.attachment.convertedJpegUrl || '';
    },
    imageUrl() {
      if (this.attachment.isBlured && !this.unblurredLink) {
        return this.attachment.link || '';
      }

      if (this.isHeicFormat(this.attachment.name)) {
        if (this.unblurredLink) {
          const convertedUnblurredUrl = this.getConvertedImage(this.unblurredLink);
          if (convertedUnblurredUrl) {
            return convertedUnblurredUrl;
          }
        }
        if (this.attachment.link) {
          const convertedUrl = this.getConvertedImage(this.attachment.link);
          if (convertedUrl) {
            return convertedUrl;
          }
        }
      }
      return this.attachment.convertedJpegUrl || this.unblurredLink || this.attachment.link || '';
    },

  },
  watch: {
    attachment: {
      handler(newVal) {
        if (newVal) {
          this.isPaused = true;
          this.playerError = false;

          if (this.activeDialog && this.internalAttachmentType === 'video') {
            this.initializeVideoPlayer();
          }
          this.triggerHeicConversion();
        }
      },
      deep: true
    },
    attachmentType: {
      handler(newVal) {
        if (newVal) {
          this.internalAttachmentType = newVal;
        }
      },
      immediate: true
    },
    currentIndex: {
      handler() {
        if (this.activeDialog && this.internalAttachmentType === 'video') {
          this.playerError = false;
          this.initializeVideoPlayer();
        }
      }
    },
    activeDialog(newVal) {
      if (newVal && this.internalAttachmentType === 'video') {
        this.playerError = false;
        this.initializeVideoPlayer();
      } else {
        this.videoReady = false;
        this.cleanupPlayer();
      }
    },
    unblurredLink: {
      handler(newVal) {
        if (newVal && this.isHeicFormat(this.attachment.name) && this.attachment && this.attachment.name) {
          this.triggerHeicConversionForUnblurred(newVal);
        }
      },
      immediate: false
    }
  },
  mounted() {
    window.addEventListener('beforeunload', this.cleanupPlayer);
  },
  beforeDestroy() {
    this.cleanupPlayer();
    window.removeEventListener('beforeunload', this.cleanupPlayer);
  },
  activated() {
    if (this.activeDialog && this.internalAttachmentType === 'video') {
      this.initializeVideoPlayer();
    }
  },
  deactivated() {
    this.videoReady = false;
  },
  methods: {
    hasPermission,
    async triggerHeicConversion() {
      if (this.attachment && this.attachment.link && this.isHeicFormat(this.attachment.name)) {
        if (!this.getConvertedImage(this.attachment.link) && !this.isConverting(this.attachment.link)) {
          try {
            await this.getDisplayImageUrl(this.attachment);
          } catch (error) {
            console.error('Automatic HEIC conversion failed:', error);
          }
        }
      }
    },
    async triggerHeicConversionForUnblurred(unblurredUrl) {
      if (unblurredUrl && this.isHeicFormat(this.attachment.name) && this.attachment && this.attachment.name) {
        if (!this.getConvertedImage(unblurredUrl) && !this.isConverting(unblurredUrl)) {
          try {
            const apiContext = {
              api: this.$api,
              caseId: this.caseId,
              galleryId: this.attachment.id,
              clientId: this.$route.query.publicId
            };

            await this.convertImage({
              originalUrl: unblurredUrl,
              filename: this.attachment.name,
              apiContext
            });
          } catch (error) {
            console.error('HEIC conversion failed for unblurred image:', error);
          }
        }
      }
    },
    openPopups() {
      this.playerError = false;
      this.videoReady = false;
      this.activeDialog = true;
      this.$nextTick(() => {
        if (this.internalAttachmentType === 'video') {
          this.initializeVideoPlayer();
        }
        this.triggerHeicConversion();
      });
    },
    cleanupPlayer() {
      this.videoReady = false;
      this.playerError = false;
    },
    closePopups() {
      this.cleanupPlayer();
      this.activeDialog = false;
      this.$emit('closed');
    },
    handleMenuOptionSelect(option) {
      this.$emit('menuOptionSelect', option);
    },
    showOriginContent() {
      this.$emit('showOriginContent', this.attachment.id);
    },
    getDeviceName(device) {
      if (typeof device === 'string') return device;
      if (device.os && device.osVersion) {
        return device.os + ' ' + device.osVersion;
      } else if (device.os) {
        return device.os;
      }
      return 'Unknown device';
    },
    prevPage() {
      if (this.currentIndex > 0) {
        this.cleanupPlayer();

        const newIndex = this.currentIndex - 1;

        this.$emit('resetUnblurred');

        this.$emit('pageChanged', {
          index: newIndex,
          item: this.galleryItems[newIndex]
        });
      }
    },
    nextPage() {
      if (this.currentIndex < this.galleryItems.length - 1) {
        this.cleanupPlayer();

        const newIndex = this.currentIndex + 1;

        this.$emit('resetUnblurred');

        this.$emit('pageChanged', {
          index: newIndex,
          item: this.galleryItems[newIndex]
        });
      }
    },
    handlePlayerError(e) {
      console.warn('Video player error:', e);

      if (!this.useNativePlayer) {
        console.log('Switching to native HTML5 video player as fallback');
        this.useNativePlayer = true;
        this.playerError = false;

        setTimeout(() => {
          if (this.activeDialog && this.internalAttachmentType === 'video') {
            this.videoReady = true;
          }
        }, 200);
      } else {
        this.playerError = true;
      }
    },

    initializeVideoPlayer() {
      this.videoReady = false;

      setTimeout(() => {
        this.videoReady = true;
      }, 200);
    },
  },
};
</script>

<style scoped>
@keyframes show {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.overlay.active {
  animation: show .3s linear;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.common-dialog-wrapper {
  width: 100%;
  max-width: 90vw;
  border: 1px solid #D7DCE6;
  box-sizing: border-box;
  border-radius: 30px;
  padding: 55px 10px;
  max-height: 90vh;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.close-dialog {
  width: 70px;
  height: 70px;
  padding: 23px;
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
}

.close-dialog svg {
  width: 100%;
  height: 100%;
}

.close-dialog svg path {
  transition: .5s all;
}

.close-dialog:hover svg path {
  fill: var(--accent-color);
}

.popup-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.popup-content img {
  width: 100%;
  height: 65vh;
  max-height: 65vh;
  object-fit: contain;
  cursor: pointer;
}

.popup-header {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  width: 100%;
  margin-bottom: 10px;
}

.popup-image {
  object-fit: contain;
}

.client-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  margin-bottom: 5px;
}

.contacts-name {
  display: flex;
  align-items: center;
}

.img-item {
  width: 24px;
  height: 24px;
  overflow: hidden;
  border: 1px solid var(--white-color);
  border-radius: 50%;
  margin-right: 8px;
}

.img-item img,
.img-item svg {
  width: 100%;
  height: 100%;
}

.img-item img {
  object-fit: cover;
}

.device-info {
  margin-left: 32px;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  color: var(--secondary-text-color);
}

.span-name {
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  color: var(--secondary-black-color);
}

.menu-items {
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: flex-end;
  padding-top: 16px;
}

.menu-items svg {
  cursor: pointer;
  transition: all .3s;
}

.tag-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-right: 15px;
}

.tag {
  font-weight: 500;
  font-size: 10px;
  line-height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #FFFFFF;
  padding: 2px 4px;
  background-color: #F1105F;
  text-transform: capitalize;
  border-radius: 4px;
  margin-bottom: 4px;
}

.tag:not(:last-child) {
  margin-right: 8px;
}

.popup-blured-content {
  background-color: #000;
  width: 100%;
  height: 65vh;
  max-height: 65vh;
  min-height: 300px;
  position: relative;
}

.popup-blured-content svg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: all .3s;
  opacity: 0;
}

.popup-blured-content:hover svg {
  opacity: 1;
}

.image-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 45px;
  min-height: 300px;
  max-height: 70vh;
}

/* Styling for vue-plyr video player */
.image-wrapper .vue-plyr {
  width: 100%;
  max-height: 65vh;
  flex-grow: 1;
  border-radius: 8px;
}

.image-wrapper .vue-plyr video,
.image-wrapper .native-video-player {
  max-height: 65vh;
  width: 100%;
  object-fit: contain;
}

.video-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.player-wrap {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  width: 100%;
  height: 65vh;
  max-height: 65vh;
}

.unsupported {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100px;
}

.unsupported span, .loading-video span {
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  text-align: center;
  vertical-align: middle;
  color: var(--secondary-black-color);
}

.loading-video {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100px;
}

.back-button,
.forward-button {
  width: 37px;
  height: 81px;
  padding: 30px 8px;
  border-radius: 8px;
  border: 1px solid #EBEFF6;
  cursor: pointer;
}

.back-button .arrow {
  width: 21px;
  height: 21px;
  transform: rotate(90deg);
}

.forward-button .arrow {
  width: 21px;
  height: 21px;
  transform: rotate(-90deg);
}

.disabled {
  opacity: 0.2;
}

.image-content {
  width: 100%;
  height: 65vh;
  max-height: 65vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.popup-conversion-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 65vh;
  max-height: 65vh;
  background-color: #f8f8f8;
  border-radius: 8px;
}
</style>
